'use client'

import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { XIcon } from '@phosphor-icons/react/dist/ssr/X'
import { cx } from 'class-variance-authority'

const DialogRoot = ({
  className,
  children,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Root> & {
  className?: string
}) => (
  <DialogPrimitive.Root {...props}>
    {className ? <div className={className}>{children}</div> : children}
  </DialogPrimitive.Root>
)
DialogRoot.displayName = DialogPrimitive.Root.displayName

const DialogPortal = DialogPrimitive.Portal

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cx(
      'fixed inset-0 z-[70] bg-background/80 backdrop-blur-sm duration-500 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className,
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    style?: string
    onClose?: () => void
  }
>(({ className, children, style, onClose, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cx(
        'pointer-events-auto fixed left-[50%] top-[50%] z-[71] grid w-full max-w-xl translate-x-[-50%] translate-y-[-50%] gap-4 border bg-white shadow-md outline-primary-500/50 duration-500 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 sm:rounded-lg md:w-full',
        className,
      )}
      {...props}
    >
      {children}
      {style === 'wide' ? (
        <DialogPrimitive.Close
          className="data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute right-3 top-2 rounded-full bg-slate-500 p-2 ring-offset-background transition-opacity hover:bg-slate-400 hover:opacity-100 focus:outline-none disabled:pointer-events-none"
          onClick={onClose}
        >
          <XIcon className="size-6 text-orange-500" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      ) : (
        <DialogPrimitive.Close
          className="data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none  disabled:pointer-events-none"
          onClick={onClose}
        >
          <XIcon className="size-6" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cx(
      'flex flex-col space-y-1.5 text-center sm:text-left',
      className,
    )}
    {...props}
  />
)
DialogHeader.displayName = 'DialogHeader'

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cx(
      'm-4 mt-0 rounded-b-md border-t border-zinc-100 pt-4',
      className,
    )}
    {...props}
  />
)
DialogFooter.displayName = 'DialogFooter'

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cx(
      'rounded-t-md border-b border-zinc-200 bg-zinc-100 p-4 text-lg leading-none text-slate-500',
      className,
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cx('p-4', className)}
    {...props}
  />
))

DialogDescription.displayName = DialogPrimitive.Description.displayName

export const Dialog = {
  Root: DialogRoot,
  Content: DialogContent,
  Header: DialogHeader,
  Footer: DialogFooter,
  Title: DialogTitle,
  Description: DialogDescription,
}
